import { useMinorUnitsToPriceString } from '@apps/card/hooks/useMinorUnitsToPriceString';
import type { PriceSummaryLineItem } from '../PriceSummary.types';
import { PriceSummaryCurrencyFormatOptions } from '../PriceSummary.constants';
import { useStationeryProductPricingContext } from '@apps/card/components/ProductUIConfig/utils/useStationeryProductPricingContext';
import { useGetValidProductShippingOptions } from '@apps/card/routes/CardCustomizer/steps/Review/components/DeliveryMethodSelector/useGetValidProductShippingOptions';
import type { CardAddress, CardDelivery } from '@apps/card/routes/CardCustomizer/CardCustomizer.types';
import type { DraftJson } from '@apps/card/routes/CardCustomizer/DraftJson.types';
import { useEffect } from 'react';
import { useEventCountryCode } from '@apps/card/hooks/useEventCountryCode';

const isPoBoxAddress = (address: CardAddress | undefined) => {
  if (!address) return false;

  const poBoxRegex = /P(.?\s?O.?)?\s?Box/i;
  return poBoxRegex.test(address.address1);
};

export const useGetCardDeliveryInfo = (
  draft: DraftJson,
  updateDeliveryMethod?: (newMethod: CardDelivery) => void,
): { lineItem: PriceSummaryLineItem; priceInMinorUnits: number; deliveryMethodId: string } | null => {
  const formatPrice = useMinorUnitsToPriceString();
  const { activeProduct } = useStationeryProductPricingContext();
  const eventCountryCode = useEventCountryCode();

  const deliveryMethodType = draft.customizations.delivery;
  const quantity = draft.customizations.quantity;

  // When no shipping address has been entered yet, fall back to the event's locale country
  // so the delivery method options (and the price summary) show domestic rates rather than
  // defaulting to the most expensive international option.
  // Normalize legacy 'USA' values the same way DeliveryMethodSelector does,
  // then fall back to the event locale when no address has been set yet.
  const rawCountryCode = draft.customizations.deliveryAddress?.countryCode;
  const countryCode = (rawCountryCode === 'USA' ? 'US' : rawCountryCode) || eventCountryCode;

  const possibleShippingOptions = useGetValidProductShippingOptions(activeProduct, {
    quantity,
    isPOBox: isPoBoxAddress(draft.customizations.deliveryAddress),
    countryCode,
  });

  const deliveryMethodConfig = possibleShippingOptions.find(o => o.id === deliveryMethodType) ?? possibleShippingOptions[0];

  // If delivery config ID doesn't match draft (Old delivery method removed),
  // then we have the config for the first possible delivery method.
  // Set draft delivery to match.
  useEffect(() => {
    if (draft.customizations.delivery && deliveryMethodConfig) {
      if (deliveryMethodConfig.id !== deliveryMethodType) {
        updateDeliveryMethod?.(deliveryMethodConfig.id as CardDelivery);
      }
    }
  }, [deliveryMethodConfig, deliveryMethodType, draft.customizations.delivery, updateDeliveryMethod]);

  if (!deliveryMethodConfig) {
    return null;
  }

  const deliveryMethodPrice = deliveryMethodConfig.price;
  const deliveryMethodPriceString = formatPrice(deliveryMethodPrice, PriceSummaryCurrencyFormatOptions);

  return { lineItem: [deliveryMethodConfig.title, deliveryMethodPriceString], priceInMinorUnits: deliveryMethodPrice, deliveryMethodId: deliveryMethodConfig.id };
};
